import React, {useEffect, useState} from 'react';
import {ReactComponent as OpenFullScreen} from '../../../../assests/svg/openFullScreen.svg';
import {
  Box,
  CssBaseline,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import BarChartCell from '../../../reports/HostingVolumeReport/BarChartCell';
import {ThemeProvider, createTheme} from '@mui/material/styles';
import CustodianDialog from '../CustodianDialog';
import {t} from 'i18next';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {getCustodianEca} from '../../../../api/EcaManagementService';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import AurDataGridPro from '../../../common/aur-data-grid-pro/AurDataGridPro';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

interface CustodianRow {
  custodianId: string;
  name: string;
  documents: number;
  documentCount: number;
}
interface CustodianData {
  custodian: CustodianRow[];
  totalDocumentsCount: number;
  uniqueCustodians: number;
}
interface EcaCustodiansProps {
  loadFileId: number | undefined;
}

type InertDivProps = React.HTMLAttributes<HTMLDivElement> & {
  inert?: string;
};

const hiddenGridProps: InertDivProps = {
  'aria-hidden': true,
  inert: '',
};

const EcaCustodians: React.FC<EcaCustodiansProps> = ({loadFileId}) => {
  const [isCustodianOpen, setIsCustodianOpen] = useState(false);
  const [loading, setLoading] = useState(true); // Start with loading as true
  const [custodianRows, setCustodianRows] = useState<CustodianRow[]>([]);
  const [isFailedToLoadContent, setIsFailedToLoadContent] = useState(false);
  const [custodianData, setCustodianData] = useState<CustodianData | null>(
    null
  );
  const {trackGAEvent} = useAnalytics();

  const handleOpenCustodian = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.ecaHomePage.expandWidget'),
        type: GA_CLICK_TYPES.ICON,
        region: GA_REGIONS.WIDGET_BODY,
        action: t('gaTracking.ecaHomePage.modalLabelwidget', {
          label: t('gaTracking.ecaHomePage.allProcessedCustodians'),
        }),
      },
    });
    setIsCustodianOpen(true);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
          label: t('gaTracking.ecaHomePage.allProcessedCustodians'),
        }),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const handleCustodianClose = () => {
    setIsCustodianOpen(false);
  };

  const {projectId, selectedProjectName: projectName} = useSelector(
    (state: RootState) => state.reports
  );
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const fetchCustodianTally = async () => {
    setLoading(true);
    setIsFailedToLoadContent(false);
    try {
      let paramString;
      if (loadFileId) {
        paramString = `clientId=${clientId}&projectId=${projectId}&loadFilesId=${loadFileId}`;
      } else {
        paramString = `clientId=${clientId}&projectId=${projectId}`;
      }
      const response = await getCustodianEca(paramString);
      const res = response.data;
      const data = res?.data || [];
      data.custodian.forEach(
        (c: CustodianRow, ind: string) => (c.custodianId = ind)
      );
      setCustodianData(data);
      setCustodianRows(data.custodian);
    } catch {
      setIsFailedToLoadContent(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustodianTally();
  }, [loadFileId, projectId]);

  const lightTheme = createTheme({
    palette: {
      mode: 'light',
    },
  });

  const sortedRows = [...custodianRows]
    .sort((a, b) => b.documentCount - a.documentCount)
    .slice(0, 5);
  const maxDocumentCount = Math.max(
    ...sortedRows.map((row) => row.documentCount)
  );

  const columns = [
    {
      field: 'name',
      headerName: t('common.tableHeaders.name'),
      flex: 1,
      headerClassName: 'no-separator',
      sortable: false,
    },
    {
      field: 'documentCount',
      headerName: t('common.tableHeaders.documentsCount'),
      flex: 3.5,
      headerClassName: 'no-separator',
      sortable: false,
      renderCell: (params: any) => {
        const rowIndex = params.rowIndex;
        return (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            aria-hidden={true}
          >
            <BarChartCell
              value={params.row.documentCount}
              maxValue={maxDocumentCount}
              name="custodian-value"
              showLabel={true}
              fullWidthBar={true}
              showTooltip={false}
              isFirst={rowIndex === 0}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <Grid item xs={6}>
      <div className="doc-column">
        <div className="doc-header">
          <div className="d-flex">
            <Typography className="typo-text subhead4_size">
              {t('ecaHomepageModals.ecaBottom.allProcessedCustodians')} &nbsp;
            </Typography>
            <Typography className="sub-text-count">
              ({custodianData?.uniqueCustodians})
            </Typography>
          </div>
          <span id="custodian-expand-hint" className="sr-only">
            {t('eca.ecaHomepage.ecaCustodian')}
          </span>

          <IconButton
            size="small"
            aria-label={`${t('ecaHomepageModals.ecaBottom.allProcessedCustodians')} ${t('buttons.expand')}`}
            aria-describedby='custodian-expand-hint'
            onClick={handleOpenCustodian}
            className="expand-button"
          >
            <OpenFullScreen />
          </IconButton>

          <CustodianDialog
            isOpen={isCustodianOpen}
            projectId={projectId}
            onClose={handleCustodianClose}
            loadFileId={loadFileId}
            projectName={projectName}
          />
        </div>

        {loading ? (
          <Box
            className="loader-center"
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="300px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <ThemeProvider theme={lightTheme}>
            <CssBaseline />
            <div {...hiddenGridProps}>
            <AurDataGridPro
              rows={sortedRows}
              getRowId={(row) => row.custodianId}
              columns={columns}
              rowHeight={35}
              hideFooter
              checkBoxVisible={false}
              columnHeaderHeight={0}
              headerFilterHeight={0}
              className="white-table-grid"
              noRowsOverlay={
                isFailedToLoadContent ? AurDataGridErrorOverlay : undefined
              }
              onErrorRefresh={() => fetchCustodianTally()}
              disableCellHeaderFocus
            />
            </div>
          </ThemeProvider>
        )}
      </div>
    </Grid>
  );
};

export default EcaCustodians;
